import { Integrators } from "./Integrator";
import { BaseFirestore } from "./Firestore";
import { ShopConfig } from "./ShopConfig";
import { AvailableLabel } from "./LabelSync";

export const CARD_FIELD_OPTIONS = [
  "RO Number",
  "Customer Last Name",
  "Customer Name",
  "Key Tag",
  "Year, Make, Model of vehicle",
  "YY Model",
  "RO Number - Tag",
  "Phone Number",
  "Custom",
] as const;

export const CARD_FIELDS = [
  { key: "primaryField", label: "Primary Field" },
  { key: "secondaryField", label: "Secondary Field" },
  { key: "tertiaryField", label: "Tertiary Field" },
] as const;

export type CardFieldKeys = (typeof CARD_FIELDS)[number]["key"];

export type CardFieldKey = (typeof CARD_FIELD_OPTIONS)[number];

export const VEHICLE_FIELDS = ["year", "make", "model"] as const;

export type VehicleFieldKey = (typeof VEHICLE_FIELDS)[number];

export const ManualCardContents = {
  manualServiceAdvisorEnabled: "manualServiceAdvisorEnabled",
  manualTechnicianEnabled: "manualTechnicianEnabled",
  manualJobHoursEnabled: "manualJobHoursEnabled",
  manualDueInTimeEnabled: "manualDueInTimeEnabled",
  manualDueOutTimeEnabled: "manualDueOutTimeEnabled",
  manualAppointmentEnabled: "manualAppointmentEnabled",
  manualTagsEnabled: "manualTagsEnabled",
  manualWaiterEnabled: "manualWaiterEnabled",
  manualCustomerInfoEnabled: "manualCustomerInfoEnabled",
  manualVehicleInfoEnabled: "manualVehicleInfoEnabled",
  manualFinancialFieldsEnabled: "manualFinancialFieldsEnabled",
  autoIncrementCardNumberEnabled: "autoIncrementCardNumberEnabled",
  manualMarkCompleteEnabled: "manualMarkCompleteEnabled",
} as const;

export type ManualCardContents =
  (typeof ManualCardContents)[keyof typeof ManualCardContents];

export type ManualCardFormat = "basic" | "advanced";

export type CardContents = {
  primaryField?: CardFieldKey | "";
  secondaryField?: CardFieldKey | "";
  tertiaryField?: CardFieldKey | "";
  primaryFieldLabel?: string | null;
  secondaryFieldLabel?: string | null;
  tertiaryFieldLabel?: string | null;
  promiseTimeEnabled?: boolean;
  serviceAdvisorEnabled?: boolean;
  technicianEnabled?: boolean;
  jobHoursEnabled?: boolean;
  manualCardFormat?: ManualCardFormat;
  waiterIconEnabled?: boolean;
  requiredVehicleFields?: VehicleFieldKey[];
} & {
  [K in ManualCardContents]?: boolean;
};

export type Container = {
  id: string;
  tags: string[];
  displayName?: string;
  linkedTechnicianId?: string | number | string[];
  linkedSourceLabel?: string | string[];
  /** The label(s) (from linkedSourceLabel) eligible to be written back to Tekmetric on a manual card move. When 2+ are configured, the user is prompted to choose one at move time. */
  bidirectionalLabel?: string | string[];
  /**
   * The subset of linkedTechnicianId eligible to be written back to
   * Tekmetric on a manual card move. `undefined` means every
   * currently-linked technician writes back (this container has never been
   * customized) -- this is a deliberately OPT-OUT default, the reverse of
   * bidirectionalLabel's opt-IN default, because technician write-back
   * already wrote back every linked technician before this field existed
   * and must not silently regress for shops already relying on it. Once
   * explicitly set (even to an empty array), this value is authoritative
   * and no longer tracks linkedTechnicianId automatically -- see
   * getBidirectionalTechnicianIds in checkContainerConfiguration.ts, the
   * single place this fallback logic lives.
   */
  bidirectionalTechnicianId?: string | number | string[];
};

export type Features = { [x: string]: boolean };

export type Layout = {
  h: number;
  i: string;
  moved: boolean;
  static: boolean;
  w: number;
  x: number;
  y: number;
};

export type DashboardLayout = { [key: string]: Layout[] };

export type Layouts = DashboardLayout[];

export type LayoutAccess = {
  layoutName: string;
  allowedList: string[];
};

export type CardConfig = {
  cardContents?: CardContents;
  timerUnits?: "minutes" | "hours";
  cardColors?: CardColor[];
  cardLabelWrap?: string;
  goListCompleteContainers?: string[];
};

export type SourceShop = {
  _source?: BaseFirestore["_source"];
  schemaVersion: 2;
  id?: string;
  shopId: string;
  email: string;
  name: string;
  phone: string;
};
export type Shop = SourceShop & {
  id: string;
  _isSoftMigrated?: boolean;
  cardConfig: CardConfig;
  shopConfig?: ShopConfig;
  containers: Container[];
  layouts: Layouts;
  layoutAccess?: LayoutAccess[];
  disabled?: boolean;
  createdOn: string;
  defaultTimer: string;
  enabledFeatures: Features;
  lastSyncedTimestamp: number;
  refreshTimeInSeconds: number;
  website?: string;
  integrationSubdomain?: string;
  logoUrl?: string;
  shopTimezone?: string;
  customLabels?: string[];
  /** Full Tekmetric custom-label catalog (id/name/color/status), pushed by the Superpowers extension. Distinct from the legacy, name-only `customLabels`. */
  labelCatalog?: AvailableLabel[];
  repairOrders: string[];
  statusesToSync: string[];
  integration?: Integrators;
  integrationKey?: "dev" | "prod";
  cardLabelWrap?: string;
  customerUpdateEnabled?: string;
  customerUpdateTimes?: string[];
  users?: string[];
  adminUserIds?: string[];
  technicianUserIds?: string[];
  officeUserIds?: string[];
  viewOnlyUserIds?: string[];
  tenantId?: string;
  ownerId?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeSubscriptionStatus?: string;
  /** @deprecated Use `subscription` instead. Kept during migration window. */
  stripeSubscription?: ShopSubscription;
  subscription?: ShopSubscription;
};

export type ShopSubscription = {
  id?: string;
  status?: string;
  plan?: string;
};

export interface CardColor {
  id: string;
  color: string;
  title: string;
  description: string;
}

/** What adminReassignShopOwner does to the shop's previous owner. */
export type PreviousOwnerHandling = "remove" | "viewOnly" | "keep";

export type AdminReassignShopOwnerRequest = {
  /** Firestore document id of the shop (NOT Shop.shopId, the integration id). */
  shopId: string;
  newOwnerEmail: string;
  previousOwner: PreviousOwnerHandling;
  /** Only applies if a new account has to be created for newOwnerEmail. */
  sendLoginEmail: boolean;
  loginLink: string;
};

export type AdminReassignShopOwnerResult = {
  ownerUid: string;
  emailSent?: boolean;
  /** False for "keep", when the new owner IS the previous owner, or when no previous-owner account existed. */
  previousOwnerUpdated: boolean;
  /** True when the previous owner's uid equals the shop doc id: the Firestore rules' `request.auth.uid == shopId` clause still grants access and claims cannot revoke it. */
  legacyAccessRetained: boolean;
};
